// ============================================================
// src/shack/shackInteraction.js
// Interacción, raycast y eventos del Shack 3D
// ============================================================

import { clampNum, projectPointToUV } from './shackSurface.js';
import { componerCuadroCanvas } from './shackFrames.js';

export function configurarInteraccion({
  canvas,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onWheel,
  onDoubleClick,
}) {
  if (!canvas) return;

  canvas.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('dblclick', onDoubleClick);
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());
}

export function configurarHotkeys(onKeyDown) {
  window.addEventListener('keydown', onKeyDown);
}

export function raycastPickables({
  canvas,
  camera,
  raycaster,
  mouse,
  pickables,
  getImageNdcFromEvent,
  event,
}) {
  if (!canvas || !camera || !raycaster || !mouse) return null;

  const pt = getImageNdcFromEvent(event);
  if (!pt) return null;

  mouse.x = pt.x;
  mouse.y = pt.y;
  raycaster.setFromCamera(mouse, camera);

  const hits = raycaster.intersectObjects(pickables || [], true);
  return hits[0] ?? null;
}

export function raycastWallPoint({
  canvas,
  camera,
  raycaster,
  mouse,
  pickWallMeshes,
  wall,
  getImageNdcFromEvent,
  event,
}) {
  if (!canvas || !camera || !raycaster || !mouse) return null;

  const pt = getImageNdcFromEvent(event);
  if (!pt) return null;

  mouse.x = pt.x;
  mouse.y = pt.y;
  raycaster.setFromCamera(mouse, camera);

  const mesh = pickWallMeshes?.[wall];
  if (!mesh) return null;

  const hits = raycaster.intersectObject(mesh, false);
  return hits[0]?.point ?? null;
}

export function getImageNdcFromEvent({
  canvas,
  host,
  toolbar,
  vp,
  imageView,
  event,
  allowOutsideImage = false,
}) {
  if (!canvas) return null;

  const rect = canvas.getBoundingClientRect();
  const stageX = (event.clientX - rect.left) / vp.escala;
  const stageY = (event.clientY - rect.top) / vp.escala;

  const x = stageX - imageView.x;
  const y = stageY - imageView.y;

  if (!allowOutsideImage) {
    if (x < 0 || y < 0 || x > imageView.w || y > imageView.h) return null;
  }

  return {
    x: (x / imageView.w) * 2 - 1,
    y: -((y / imageView.h) * 2 - 1),
  };
}

export function getStagePointFromEvent({
  host,
  toolbar,
  vp,
  event,
}) {
  if (!host) return null;

  const rect = host.getBoundingClientRect();
  const xHost = event.clientX - rect.left;
  const yHost = event.clientY - rect.top - (toolbar?.offsetHeight || 0);

  return {
    x: (xHost - vp.tx) / vp.escala,
    y: (yHost - vp.ty) / vp.escala,
  };
}

export function crearHandlersInteraccion(ctx) {
  const {
    state,
    modalVisible,
    selectedItem,
    itemByKey,
    meshByItemKey,
    mostrarGrid,
    ocultarGrid,
    actualizarHelperRect,
    guardarCalibracionLocal,
    actualizarVisualCalibracion,
    actualizarVisualCalibracionLigera,
    guardarDebounced,
    renderizarItems,
    aplicarViewport,
    aplicarCameraViewOffset,
    resetearViewport,
    abrirMapaZoom,
    abrirItemFullscreen,
    deleteSelected,
    actualizarToolbarEstado,
    toggleCalib = null,
    actualizarMultiSelectRects = null,
    mostrarToast = null,
    aplicarTransformItem,
    round4,
    surfacePoint,
    mapUrlFallback,
    minTone,
    maxTone,
    minW,
    maxW,
    zoomMin = 1,
    zoomMax = 4,
    zoomStep = 0.12,
    readOnly = false,
  } = ctx;

  let calibrationRafId = 0;

  function reaplicarTransformsItems() {
    state.items.forEach((item) => {
      const mesh = meshByItemKey(item.shackKey);
      if (mesh) aplicarTransformItem(mesh, item);
    });
  }

  function pedirActualizacionCalibracionLigera() {
    if (calibrationRafId) return;

    calibrationRafId = requestAnimationFrame(() => {
      calibrationRafId = 0;

      reaplicarTransformsItems();

      if (typeof actualizarVisualCalibracionLigera === 'function') {
        actualizarVisualCalibracionLigera();
      } else if (typeof actualizarVisualCalibracion === 'function') {
        actualizarVisualCalibracion();
      }
    });
  }

  function cancelarActualizacionCalibracionLigera() {
    if (!calibrationRafId) return;
    cancelAnimationFrame(calibrationRafId);
    calibrationRafId = 0;
  }

  function onPointerDown(e) {
    if (!modalVisible()) return;
    if (e.button !== 0) return;

    const hit = raycastPickables({
      canvas: state.canvas,
      camera: state.camera,
      raycaster: state.raycaster,
      mouse: state.mouse,
      pickables: state.pickables,
      getImageNdcFromEvent: (event) =>
        getImageNdcFromEvent({
          canvas: state.canvas,
          host: state.host,
          toolbar: state.toolbar,
          vp: state.vp,
          imageView: state.imageView,
          event,
        }),
      event: e,
    });

    if (!hit || readOnly) {
      if (!readOnly && (state.selection.itemKey || state.selection.selectedKeys?.size > 0)) {
        state.selection.itemKey = null;
        state.selection.selectedKeys?.clear();
        if (state.helperRect) state.helperRect.visible = false;
        actualizarToolbarEstado();
        actualizarMultiSelectRects?.();
      }

      state.drag.activo = true;
      state.drag.tipo = 'pan';
      state.drag.startX = e.clientX;
      state.drag.startY = e.clientY;
      state.drag.startPx = state.vp.px ?? 0;
      state.drag.startPy = state.vp.py ?? 0;

      if (state.canvas) state.canvas.style.cursor = 'grabbing';
      return;
    }

    const item = itemByKey(hit.object.userData.itemKey);
    if (!item) return;

    // Ctrl / Cmd: toggle item en la multi-selección
    if (e.ctrlKey || e.metaKey) {
      const key = item.shackKey;
      const set = state.selection.selectedKeys;
      if (set.has(key)) {
        set.delete(key);
        if (state.selection.itemKey === key) {
          const remaining = [...set];
          state.selection.itemKey = remaining.length ? remaining[remaining.length - 1] : null;
        }
      } else {
        const primaryWall = state.selection.itemKey ? itemByKey(state.selection.itemKey)?.wall : null;
        if (primaryWall && item.wall !== primaryWall) {
          mostrarToast?.('La selección múltiple debe estar en la misma pared.', 'error');
          return;
        }
        if (state.selection.itemKey) set.add(state.selection.itemKey);
        set.add(key);
        state.selection.itemKey = key;
      }
      actualizarToolbarEstado();
      actualizarMultiSelectRects?.();
      // Actualizar helperRect al item primario vigente tras el toggle
      const primario = state.selection.itemKey ? itemByKey(state.selection.itemKey) : null;
      if (primario) actualizarHelperRect(primario);
      else if (state.helperRect) state.helperRect.visible = false;
      return;
    }

    // Shift: agregar item a la multi-selección
    if (e.shiftKey) {
      const primaryWall = state.selection.itemKey ? itemByKey(state.selection.itemKey)?.wall : null;
      if (primaryWall && item.wall !== primaryWall) {
        mostrarToast?.('La selección múltiple debe estar en la misma pared.', 'error');
        return;
      }
      const set = state.selection.selectedKeys;
      if (state.selection.itemKey) set.add(state.selection.itemKey);
      set.add(item.shackKey);
      state.selection.itemKey = item.shackKey;
      actualizarToolbarEstado();
      actualizarMultiSelectRects?.();
      // El primario vigente es siempre el item recién agregado
      actualizarHelperRect(item);
      return;
    }

    // Click normal: si el item ya está en la selección se mantiene el grupo;
    // si no, se limpia y se selecciona solo este item.
    const isInSelection =
      item.shackKey === state.selection.itemKey ||
      state.selection.selectedKeys?.has(item.shackKey);

    if (!isInSelection) {
      state.selection.selectedKeys?.clear();
      actualizarMultiSelectRects?.();
    }
    state.selection.itemKey = item.shackKey;
    actualizarToolbarEstado();

    // Registrar posiciones iniciales para arrastre de grupo
    state.drag.startU = item.u;
    state.drag.startV = item.v;
    state.drag.startUVs = null;
    const selSet = state.selection.selectedKeys;
    if (selSet?.size > 0) {
      state.drag.startUVs = new Map();
      for (const key of selSet) {
        if (key === item.shackKey) continue;
        const si = itemByKey(key);
        if (si) state.drag.startUVs.set(key, { u: si.u, v: si.v });
      }
    }

    state.drag.activo = true;
    state.drag.tipo = 'item';
    state.drag.itemKey = item.shackKey;
    state.drag.wall = item.wall;

    actualizarHelperRect(item);
    if (isInSelection) actualizarMultiSelectRects?.();

    if (state.canvas) state.canvas.style.cursor = 'grabbing';
  }

  function onPointerMove(e) {
    if (state.drag.activo && state.drag.tipo === 'handle') {
      const point = raycastWallPoint({
        canvas: state.canvas,
        camera: state.camera,
        raycaster: state.raycaster,
        mouse: state.mouse,
        pickWallMeshes: state.pickWallMeshes,
        wall: state.drag.wall,
        getImageNdcFromEvent: (event) =>
          getImageNdcFromEvent({
            canvas: state.canvas,
            host: state.host,
            toolbar: state.toolbar,
            vp: state.vp,
            imageView: state.imageView,
            event,
            allowOutsideImage: true,
          }),
        event: e,
      });

      if (!point) return;

      const c = state.calibration[state.drag.wall];
      c[state.drag.cornerKey] = {
        x: round4(point.x),
        y: round4(point.y),
        z: round4(point.z),
      };

      pedirActualizacionCalibracionLigera();
      return;
    }

    if (state.drag.activo && state.drag.tipo === 'pan') {
      state.vp.px = state.drag.startPx + (e.clientX - state.drag.startX);
      state.vp.py = state.drag.startPy + (e.clientY - state.drag.startY);
      aplicarCameraViewOffset();
      return;
    }

    if (state.drag.activo && state.drag.tipo === 'item') {
      const item = itemByKey(state.drag.itemKey);
      if (!item) return;

      const stagePt = getStagePointFromEvent({
        host: state.host,
        toolbar: state.toolbar,
        vp: state.vp,
        event: e,
      });

      if (!stagePt) return;

      const nextWall =
        stagePt.x < state.imageView.x + state.imageView.w * 0.69
          ? 'trasera'
          : 'lateral';

      item.wall = nextWall;

      const point = raycastWallPoint({
        canvas: state.canvas,
        camera: state.camera,
        raycaster: state.raycaster,
        mouse: state.mouse,
        pickWallMeshes: state.pickWallMeshes,
        wall: item.wall,
        getImageNdcFromEvent: (event) =>
          getImageNdcFromEvent({
            canvas: state.canvas,
            host: state.host,
            toolbar: state.toolbar,
            vp: state.vp,
            imageView: state.imageView,
            event,
          }),
        event: e,
      });

      if (!point) return;

      const uv = projectPointToUV(
        state.calibration,
        item.wall,
        point,
        item.w,
        item.h,
        item.u,
        item.v
      );

      const startU0 = state.drag.startU ?? item.u;
      const startV0 = state.drag.startV ?? item.v;
      let du = uv.u - startU0;
      let dv = uv.v - startV0;

      // Limitar delta para que ningún item del grupo salga de [0, 1]
      function limitarDelta(delta, start) {
        if (delta > 0) return Math.min(delta, 1 - start);
        if (delta < 0) return Math.max(delta, -start);
        return 0;
      }

      du = limitarDelta(du, startU0);
      dv = limitarDelta(dv, startV0);

      if (state.drag.startUVs?.size > 0) {
        for (const [, startPos] of state.drag.startUVs) {
          du = limitarDelta(du, startPos.u);
          dv = limitarDelta(dv, startPos.v);
        }
      }

      item.u = startU0 + du;
      item.v = startV0 + dv;

      const mesh = meshByItemKey(item.shackKey);
      if (mesh) aplicarTransformItem(mesh, item);

      // Mover todos los items secundarios del grupo con el mismo delta UV
      if (state.drag.startUVs?.size > 0) {
        for (const [key, startPos] of state.drag.startUVs) {
          const si = itemByKey(key);
          if (!si) continue;
          si.u = startPos.u + du;
          si.v = startPos.v + dv;
          const sm = meshByItemKey(key);
          if (sm) aplicarTransformItem(sm, si);
        }
        actualizarMultiSelectRects?.();
      }

      mostrarGrid(item.wall);
      actualizarHelperRect(item);
      return;
    }

    if (!state.drag.activo && state.canvas) {
      const hit = raycastPickables({
        canvas: state.canvas,
        camera: state.camera,
        raycaster: state.raycaster,
        mouse: state.mouse,
        pickables: state.pickables,
        getImageNdcFromEvent: (event) =>
          getImageNdcFromEvent({
            canvas: state.canvas,
            host: state.host,
            toolbar: state.toolbar,
            vp: state.vp,
            imageView: state.imageView,
            event,
          }),
        event: e,
      });

      state.canvas.style.cursor = hit ? 'grab' : 'default';
    }
  }

  function onPointerUp() {
    if (!state.drag.activo) return;

    const wasItem = state.drag.tipo === 'item';
    const wasHandle = state.drag.tipo === 'handle';

    state.drag.activo = false;
    state.drag.tipo = null;
    state.drag.itemKey = null;
    state.drag.wall = null;
    state.drag.cornerKey = null;
    state.drag.startUVs = null;

    if (state.canvas) state.canvas.style.cursor = 'default';

    if (wasHandle) {
      cancelarActualizacionCalibracionLigera();
      guardarCalibracionLocal();
      reaplicarTransformsItems();

      if (typeof actualizarVisualCalibracion === 'function') {
        actualizarVisualCalibracion({ relayout: false, recenter: false, rerenderItems: false });
      }

      return;
    }

    if (wasItem && !readOnly) {
      ocultarGrid();
      guardarDebounced();
    }
  }

  function onWheel(e) {
    if (!modalVisible()) return;

    const item = selectedItem();

    if (item && !readOnly) {
      e.preventDefault();

      if (e.altKey) {
        const deltaTone = e.deltaY < 0 ? 0.05 : -0.05;
        item.frameTone = clampNum(item.frameTone + deltaTone, minTone, maxTone);
        renderizarItems(state.thumbs);
        guardarDebounced();
        return;
      }

      const factor = e.deltaY < 0 ? 1.045 : 0.957;

      function aplicarFactorItem(it) {
        it.w = clampNum(it.w * factor, minW, maxW);
        if (it.frameRatio && it.frameRatio > 0) {
          it.h = it.w / it.frameRatio;
        } else if (it.ratio && it.ratio > 0) {
          it.h = it.w / it.ratio;
        } else if (it.h && it.w) {
          const ratioActual = it.w / it.h;
          it.h = it.w / ratioActual;
        }
      }

      const keysParaResize = new Set(state.selection.selectedKeys ?? []);
      keysParaResize.add(item.shackKey);
      for (const key of keysParaResize) {
        const it = key === item.shackKey ? item : itemByKey(key);
        if (it) aplicarFactorItem(it);
      }
      if ((state.selection.selectedKeys?.size ?? 0) > 0) actualizarMultiSelectRects?.();

      renderizarItems(state.thumbs);
      guardarDebounced();
      return;
    }

    e.preventDefault();

    const dir = e.deltaY < 0 ? 1 : -1;
    const factor = dir > 0 ? 1 + zoomStep : 1 / (1 + zoomStep);
    const nuevoZoom = clampNum((state.vp.zoom ?? 1) * factor, zoomMin, zoomMax);

    if (nuevoZoom === state.vp.zoom) return;

    const oldZoom = state.vp.zoom ?? 1;
    const scale = nuevoZoom / oldZoom;

    state.vp.px = (state.vp.px ?? 0) * scale;
    state.vp.py = (state.vp.py ?? 0) * scale;

    state.vp.zoom = nuevoZoom;
    aplicarCameraViewOffset();
  }

  async function onDoubleClick(e) {
    const hit = raycastPickables({
      canvas: state.canvas,
      camera: state.camera,
      raycaster: state.raycaster,
      mouse: state.mouse,
      pickables: state.pickables,
      getImageNdcFromEvent: (event) =>
        getImageNdcFromEvent({
          canvas: state.canvas,
          host: state.host,
          toolbar: state.toolbar,
          vp: state.vp,
          imageView: state.imageView,
          event,
        }),
      event: e,
    });

    if (!hit) {
      resetearViewport();
      return;
    }

    const item = itemByKey(hit.object.userData.itemKey);
    if (!item) return;

    let imgOrUrl = null;
    if (item.tipo === 'mapa') {
      imgOrUrl = item.src || mapUrlFallback;
    } else if (item.tipo === 'certificado') {
      imgOrUrl = state.imagenesCache.get(item.fileId) || state.thumbs.get(item.fileId) || null;
    }

    try {
      const canvas = await componerCuadroCanvas(item, imgOrUrl);
      const blobUrl = await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => blob ? resolve(URL.createObjectURL(blob)) : reject(new Error('toBlob')), 'image/png');
      });
      canvas.width = 0;
      canvas.height = 0;
      abrirItemFullscreen(blobUrl, item.nombre || item.titulo || item.concurso || '');
    } catch (err) {
      console.warn('[Shack3D] fullscreen compose error:', err);
    }
  }

  function onKeyDown(e) {
    if (!modalVisible()) return;
    if (readOnly) return;

    if (e.key === 'Escape') {
      const hadSel = state.selection.itemKey || state.selection.selectedKeys?.size > 0;
      state.selection.selectedKeys?.clear();
      state.selection.itemKey = null;
      if (hadSel) {
        if (state.helperRect) state.helperRect.visible = false;
        actualizarToolbarEstado();
        actualizarMultiSelectRects?.();
      }
      return;
    }

    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (selectedItem()) {
        e.preventDefault();
        deleteSelected();
      }
      return;
    }

    if (e.key.toLowerCase() === 'g') {
      toggleCalib?.();
      return;
    }

    const item = selectedItem();
    if (!item) return;

    const paso = e.shiftKey ? 0.01 : 0.004;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      item.u -= paso;
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      item.u += paso;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      item.v -= paso;
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      item.v += paso;
    } else {
      return;
    }

    const uv = projectPointToUV(
      state.calibration,
      item.wall,
      surfacePoint(state.calibration, item.wall, item.u, item.v),
      item.w,
      item.h,
      item.u,
      item.v
    );

    item.u = uv.u;
    item.v = uv.v;

    const mesh = meshByItemKey(item.shackKey);
    if (mesh) aplicarTransformItem(mesh, item);

    actualizarHelperRect(item);
    guardarDebounced();
  }

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onWheel,
    onDoubleClick,
    onKeyDown,
  };
}